/*
 * CS Util Lib
 */
"use strict";

class csQuery {
  constructor(selector, context = document) {
    this.selector = selector;

    if (Array.isArray(context)) {
      this.elems = context;
    } else {
      this.elems = Array.from(context.querySelectorAll(selector));
    }
  }

  isDefined(...args) {
    if (args.length === 0) {
      return this.elems.length > 0;
    } else {
      return this.find(args[0]).isDefined()
    }
  }

  find(selector) {
    return new csQuery(selector, this.container());
  }

  // filter('.li')
  // filter(function(item, index) { return ...}))
  filter(selector) {
    const result = [];

    if (typeof selector === "string") {
      this.each(function (item) {
        if (item.matches(selector)) {
          result.push(item);
        }
      })
      return new csQuery(selector, result);
    } else if (typeof selector === "function") {
      for (let i = 0; i < this.elems.length; i++) {
        if (selector(this.elems[i], i)) {
          result.push(this.elems[i]);
        }
      }
      return new csQuery(this.selector, result);
    }
  }

  // first()
  // first('li')
  // first(function(item, index) { return ...}))
  first(selector) {
    if (selector) {
      return this.filter(selector).first();
    } else {
      return this.elems[0]
    }
  }

  // last()
  // last('li')
  // last(function(item, index) { return ...}))
  last(selector) {
    if (selector) {
      return this.filter(selector).last();
    } else {
      return this.elems[this.elems.length - 1]
    }
  }

  // attr('name')
  // attr('name', 'user[firstname]')
  attr(...args) {
    if (args.length === 1) {
      return this.container().getAttribute(args[0]);
    } else {
      this.each(function (item) {
        item.setAttribute(args[0], args[1]);
      })
      return this;
    }
  }

  // removeAttr('name')
  // removeAttr('name', 'hidden')
  removeAttr(...names) {
    this.each(function (item) {
      for (let name of names) {
        item.removeAttribute(name);
      }
    })
    return this;
  }

  // data('url')
  // data('url', '/path/to/action')
  data(...args) {
    if (args.length === 1) {
      return this.attr(`data-${args[0]}`);
    } else {
      this.attr(`data-${args[0]}`, args);
      return this;
    }
  }

  // value()
  // value(42)
  value(...args) {
    if (args.length === 1) {
      for (let item of this.elems) {
        item.value = args[0];
      }
      return this;
    } else {
      return this.container().value;
    }
  }

  html(...args) {
    if (args.length === 1) {
      for (let item of this.elems) {
        item.innerHTML = args[0];
      }
      return this;
    } else {
      return this.container().innerHTML;
    }
  }

  focus() {
    this.container().focus();
    return this;
  }

  addClass(...classes) {
    this.each(function (item) {
      item.classList.add(...classes);
    })
    return this;
  }

  removeClass(...classes) {
    this.each(function (item) {
      item.classList.remove(...classes);
    })
    return this;
  }

  toggleClass(...names) {
    this.each(function (item) {
      for (let name of names) {
        item.classList.toggle(name);
      }
    })
    return this;
  }

  containsClass(name) {
    return this.container().contains(name);
  }

  // on('click', function(e) {...})
  // on('click', '.js-action-save', function(e) {...})
  on(...args) {
    const that = this;

    if (args.length === 2) {
      this.each(function (item) {
        item.addEventListener(args[0], args[1])
      })
    } else if (args.length === 3) {
      for (let item of this.elems) {
        item.addEventListener(args[0], function (event) {
          if (event.target.matches(args[1]) || event.target.closest(args[1])) {
            args[2](event);
          }
        })
      }
    } else {
      throw new Error(`csQuery: on() has invalid arity of ${args.length} arguments`);
    }
    return this;
  }

  off(...args) {
    if (args.length === 2) {
      this.container().removeEventListener(args[0], args[1]);
    } else if (args.length === 3) {
      this.container().removeEventListener(args[0], function (event) {
        if (event.target.matches(args[1]) || event.target.closest(args[1])) {
          args[2](event);
        }
      });
    } else {
      throw new Error(`csQuery: off() has invalid arity of ${args.length} arguments`);
    }
    return this;
  }

  // Simulate: jQuery(this.selector).delay(2000).fadeOut({duration: 600});
  fadeOut(options) {
    let element = this.elems[0];

    if (element === undefined) { return this }

    setTimeout(() => {
      element.style.transition = `opacity ${options.duration}ms`;
      element.style.opacity = "0"; // Fades out by reducing opacity

      setTimeout(() => { element.style.display = "none"; }, options.duration);
    }, options.delay);

    return this;
  }

  container() {
    if (this.elems[0] === undefined) {
      throw new Error(`csQuery: Undefined container "${this.selector}"`);
    }
    return this.elems[0];
  }

  each(callback) {
    for (let item of this.elems) {
      callback(item);
    }
  }
}

function q(selector, context = document) {
  const query = new csQuery(selector, context);

  if (query.isDefined()) {
    return query
  } else {
    return undefined;
  }
}

function bool(value) {
  if (value === null || value === undefined) {
    return false;
  }

  const str = String(value).trim();

  if (str === '0' || str === 'false') {
    return false;
  }

  return true;
}

// Example: {"filter[policy_number]":"10002391","filter[address][street]":""}
// See https://gomakethings.com/how-to-serialize-form-data-with-vanilla-js/
function serializeForm(formElement) {
  const data = new FormData(formElement);
  let obj = {};

  for (let [key, value] of data) {
    if (obj[key] !== undefined) {
      if (!Array.isArray(obj[key])) {
        obj[key] = [obj[key]];
      }
      obj[key].push(value);
    } else {
      obj[key] = value;
    }
  }
  return obj;
}

// Example: [
//   {"name":"filter[policy_number]","value":"10002450"},
//   {"name":"filter[address][street]","value":""}
// ]
function serializeArray(form) {
  const formData = new FormData(form);

  const pairs = [];

  for (const [name, value] of formData) {
    pairs.push({name, value});
  }

  return pairs;
}

export { bool, q, serializeArray, serializeForm }
